import { useEffect, useState } from "react";
import { timeAgo } from "../services/helperFunctions";
import silu from '../../../../assets/silu.png';
import whats from '../../../../assets/whats.png';

// Helper function to check if message is an image
const isImageMessage = (message) => {
  if (!message || typeof message !== 'string') return false;
  
  // Check for base64 data URL prefix
  if (message.startsWith('data:image/')) {
    return true;
  }
  
  // Check for image file extensions
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
  if (imageExtensions.some(ext => message.toLowerCase().includes(ext))) {
    return true;
  }
  
  // Check for wp_media_ indicator
  return message.includes('wp_media_');
};

// Helper function to check if message is a video
const isVideoMessage = (message) => {
  if (!message || typeof message !== 'string') return false;
  
  // Check for base64 data URL prefix
  if (message.startsWith('data:video/')) {
    return true;
  }
  
  // Check for common video file extensions
  const lowerCaseMessage = message.toLowerCase();
  return /\.(mp4|webm|mov|avi|mkv|m4v)(\?|$)/.test(lowerCaseMessage);
};

// Helper function to check if message is an audio
const isAudioMessage = (message) => {
  if (!message || typeof message !== 'string') return false;
  
  // Check for base64 data URL prefix
  if (message.startsWith('data:audio/')) {
    return true;
  }
  
  // Check for common audio file extensions
  const lowerCaseMessage = message.toLowerCase();
  return /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/.test(lowerCaseMessage);
};

function isPhoneNumber(str) {
  // Simple check: only digits, spaces, +, -, or ()
  return /^[\d\s+\-()]+$/.test(str);
}

const FeedChatButton = ({ message, currentChat, handleSelect }) => { 
  const [isSmallDevice, setIsSmallDevice] = useState(false);
  const [imageError, setImageError] = useState(false);   

  useEffect(() => {
    const handleResize = () => setIsSmallDevice(window.innerWidth < 640);
    handleResize(); 
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); 

  // Safe message content rendering
  const renderMessageContent = () => {
    if (!message.message) return 'No message';
    
    if (isVideoMessage(message.message)) {
      return (
        <div className="flex items-center gap-2 text-gray-600">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="italic">Video</span>
        </div>
      );
    }
    
    if (isAudioMessage(message.message)) {
      return (
        <div className="flex items-center gap-2 text-gray-600">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          <span className="italic">Audio</span>
        </div>
      );
    }
    
    if (isImageMessage(message.message)) {
      return (
        <div className="flex items-center gap-2 text-gray-600">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="italic">Image</span>
        </div>
      );
    }
    
    if (message.message.includes("longitude") && message.message.includes("latitude")) {
      return (
        <div className="flex items-center gap-2 text-gray-600">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="italic">Location</span>
        </div>
      );
    }
    
    // Safe text rendering - strip HTML tags for preview
    const textContent = message.message.replace(/<[^>]*>/g, '').trim();
    return <span className="break-words">{textContent}</span>;
  };

  return ( 
    <div
      onClick={() => handleSelect(message)}
      className={`flex items-start gap-3 bg-white rounded-xl shadow-sm border border-neutral-200 p-4 transition-all duration-200 hover:shadow-md hover:border-sky-300 cursor-pointer ${
        message.channel_id === currentChat?.channel_id ? "ring-2 ring-sky-400 border-sky-300 shadow-md" : ""
      }`}
    >
      {/* Avatar */}
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center overflow-hidden">
        <img
          className="w-full h-full rounded-full object-cover"
          src={message.category === 1 ? silu : whats}
          alt={`${message.category === 1 ? 'Silu' : 'WhatsApp'} chat`}
          onError={() => setImageError(true)}
        />
      </div>
      
      {/* Chat info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex-1 min-w-0">
            {/* Customer name as primary display */}
            <div className="font-semibold text-gray-900 truncate text-sm">
              {message.customer_name || message.name || 'Unknown Customer'}
            </div>
            
            {/* Phone number for WhatsApp chats */}
            {message.category === 2 && message.channel_id && (
              <div className="text-xs text-gray-500 mt-0.5 font-mono">
                {message.channel_id}
              </div>
            )}
            
            {/* Agent info as compact badge */}
            {message.agent_name && (
              <div className="mt-1">
                <span className="inline-block px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
                  {message.agent_name}
                </span>
              </div>
            )}
          </div>
          
          <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
            {timeAgo(message.timestamp)}
          </span>
        </div>
        
        <div className="text-sm text-gray-600 leading-relaxed">
          {renderMessageContent()}
        </div>
      </div>
      
      {/* Unread badge */}
      {message.unread > 0 && message.direction === 0 && (
        <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm flex-shrink-0">
          {message.unread > 99 ? '99+' : message.unread}
        </span>
      )}
    </div>
  );
};

export default FeedChatButton;
